import type { Body, World } from "fast-2d";
import type { Camera } from "../render/Camera";
import type { CanvasRenderer } from "../render/CanvasRenderer";

export interface SceneAction {
	label: string;
	run: () => void;
	variant?: "danger" | "success";
}

export const demoCategories = [
	"Featured",
	"Action & Destruction",
	"Stacking & Stress",
	"Joints & Machines",
	"Playground & Toys",
] as const;

export type DemoCategory = (typeof demoCategories)[number];

/** A built, runnable scene: a world, a camera, and optional interaction. */
export interface Scene {
	world: World;
	/** Additional worlds owned by custom scenes, included in aggregate stats. */
	worlds?: readonly World[];
	camera: Camera;
	/** Called once per animation frame before `world.update`, for emitters etc. */
	onBeforeStep?: (dt: number) => void;
	/** Optional custom update loop for scenes that own more than one world. */
	update?: (dt: number) => number;
	/** Pointer pressed at the given world coordinates. */
	onPointerDown?: (x: number, y: number) => void;
	onPointerMove?: (x: number, y: number) => void;
	onPointerUp?: (x: number, y: number) => void;
	/**
	 * Optional custom overlay, drawn after the world each frame. Receives the
	 * Canvas2D context (already scaled for devicePixelRatio) and the camera, so
	 * demos can visualize queries, rays, sensors, etc.
	 */
	draw?: (ctx: CanvasRenderingContext2D, camera: Camera) => void;
	/** Optional custom renderer for scenes that need multiple viewports/worlds. */
	render?: (renderer: CanvasRenderer) => void;
	/**
	 * Bodies the default renderer should skip, leaving them entirely to `draw`.
	 * Used by demos with bespoke artwork (e.g. gears) so the plain body shape
	 * doesn't show through behind the custom rendering.
	 */
	hiddenBodies?: ReadonlySet<Body>;
	/** Optional one-line usage hint shown over the canvas. */
	hint?: string;
	/**
	 * Whether bodies can be grabbed and thrown with the pointer (default `true`).
	 * Demos that drive the cursor themselves (e.g. the raycast radar) opt out.
	 */
	draggable?: boolean;
	/** Optional buttons shown next to Pause/Reset for scene-specific actions. */
	actions?: () => readonly SceneAction[];
}

/** A demo: metadata plus a factory that builds a fresh `Scene`. */
export interface Demo {
	id: string;
	title: string;
	blurb: string;
	category: DemoCategory;
	build: () => Scene;
}
